import * as React from "react";

import { cn } from "./cn";
import { accountLabel, formatMinor } from "./format";

type PeriodRange = { from: string; to: string };

/**
 * Where a month's books stand. complete: every account balanced at month end
 * and nothing left to review; open: work left; current: still running;
 * before_books: before the books start (faded, not selectable).
 */
type PeriodMonthStatus = "complete" | "open" | "current" | "before_books";

/** What is left on an open month, as the dashboard sends it. */
type PeriodMonthTodo = {
  toReview?: number | null;
  waitingOnCustomer?: number | null;
  unbalancedAccounts?: Array<{
    name?: string | null;
    mask?: string | null;
    differenceMinor?: number | null;
  }> | null;
} | null;

type PeriodTimelineMonth = {
  /** "2026-01" */
  key: string;
  /** "Jan" */
  label: string;
  hasData?: boolean;
  /** No status yet (an older API): no mark, but its row is still reserved. */
  status?: PeriodMonthStatus;
  /** An open month's tooltip, e.g. from `monthTodoText`. */
  hint?: string | null;
};

/**
 * "14 to review · 3 waiting on the customer · Plaid checking ••1000 off by
 * $500.00"; null when nothing is left.
 */
function monthTodoText(todo: PeriodMonthTodo | undefined): string | null {
  if (!todo) return null;
  const parts: string[] = [];
  if (todo.toReview)
    parts.push(`${todo.toReview.toLocaleString("en-US")} to review`);
  if (todo.waitingOnCustomer)
    parts.push(
      `${todo.waitingOnCustomer.toLocaleString("en-US")} waiting on the customer`,
    );
  for (const account of todo.unbalancedAccounts ?? []) {
    const name = accountLabel(account.name, account.mask, "An account");
    parts.push(
      typeof account.differenceMinor === "number"
        ? `${name} off by $${formatMinor(Math.abs(account.differenceMinor))}`
        : `${name} no bank balance`,
    );
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** An ordered {from, to} for two keys given in either direction. */
function rangeBetween(
  keys: readonly string[],
  a: string,
  b: string,
): PeriodRange {
  const ia = keys.indexOf(a);
  const ib = keys.indexOf(b);
  const forward = ia === -1 || ib === -1 ? a <= b : ia <= ib;
  return forward ? { from: a, to: b } : { from: b, to: a };
}

/**
 * One step along the keys. Plain: the whole window slides, and stays put at
 * an end. Extend: the `to` edge grows or shrinks, never below `from`.
 */
function stepRange(
  keys: readonly string[],
  range: PeriodRange,
  delta: 1 | -1,
  extend: boolean,
): PeriodRange {
  const from = keys.indexOf(range.from);
  const to = keys.indexOf(range.to);
  if (from === -1 || to === -1) return range;
  const last = keys.length - 1;
  if (extend) {
    const next = Math.min(Math.max(to + delta, from), last);
    return { from: range.from, to: keys[next] ?? range.to };
  }
  if (from + delta < 0 || to + delta > last) return range;
  return {
    from: keys[from + delta] ?? range.from,
    to: keys[to + delta] ?? range.to,
  };
}

const MONTH_WIDTH = 52;

type PeriodTimelineProps = {
  ariaLabel: string;
  /** Oldest to newest. */
  months: PeriodTimelineMonth[];
  value: PeriodRange;
  onChange: (range: PeriodRange) => void;
  className?: string;
};

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * "Aug" for a `2025-08` key, whatever the label says (the API sends
 * "Aug 2025"; the strip marks the year above January instead). Any other key
 * shows its label.
 */
export function shortMonth(month: Pick<PeriodTimelineMonth, "key" | "label">) {
  const match = /^\d{4}-(\d{2})$/.exec(month.key);
  const name = match ? SHORT_MONTHS[Number(match[1]) - 1] : undefined;
  return name ?? month.label;
}

function monthText(month: PeriodTimelineMonth | undefined) {
  return month ? `${shortMonth(month)} ${month.key.slice(0, 4)}` : "";
}

const CHECK = (
  <svg
    aria-hidden="true"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7.4l2.6 2.6L11 4.6" />
  </svg>
);

/** The status mark under a month's label; nothing for before_books or unknown. */
function MonthMark({ status }: { status: PeriodMonthStatus | undefined }) {
  if (status === "complete")
    return (
      <span
        data-mark="complete"
        className="grid place-items-center text-primary"
      >
        {CHECK}
      </span>
    );
  if (status === "open")
    return (
      <span data-mark="open" className="size-1.5 rounded-full bg-warning" />
    );
  if (status === "current")
    return (
      <span
        data-mark="current"
        className="size-1.5 rounded-full border-[1.5px] border-muted-foreground"
      />
    );
  return null;
}

const STATUS_WORD: Record<PeriodMonthStatus, string> = {
  complete: "complete",
  open: "open",
  current: "this month",
  before_books: "before the books start",
};

/** The nearest month that can be picked: the books' first month for an earlier one. */
function nearestSelectable(
  months: readonly PeriodTimelineMonth[],
  index: number,
): string | null {
  for (let i = index; i < months.length; i += 1)
    if (months[i]?.status !== "before_books") return months[i]?.key ?? null;
  for (let i = index - 1; i >= 0; i -= 1)
    if (months[i]?.status !== "before_books") return months[i]?.key ?? null;
  return null;
}

/**
 * A strip of months, one equal cell each: the label centred with its status
 * mark under it. Click picks a month, drag brushes a range, shift+click
 * extends from the start; the selection box (a slider for the keyboard) fills
 * the chosen cells inset 3px and moves by transform only, so a pick is never
 * a layout shift. Months before the books are faded and never selected. An
 * open month says what is left on hover, or on focus when it ends the range.
 * Scrolls inside itself on a phone.
 */
function PeriodTimeline({
  ariaLabel,
  months,
  value,
  onChange,
  className,
}: PeriodTimelineProps) {
  const keys = React.useMemo(() => months.map((m) => m.key), [months]);
  const selectable = React.useMemo(
    () => months.filter((m) => m.status !== "before_books").map((m) => m.key),
    [months],
  );
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const stripRef = React.useRef<HTMLDivElement>(null);
  const brush = React.useRef<{ anchor: string; head: string } | null>(null);
  const tipId = React.useId();
  const [preview, setPreview] = React.useState<PeriodRange | null>(null);
  // A pick is held until the page hands back a new `value`, so the box never
  // shows the old range for a frame between pointer-up and the re-render.
  const [held, setHeld] = React.useState<PeriodRange | null>(null);
  const [lastValue, setLastValue] = React.useState(value);
  if (lastValue.from !== value.from || lastValue.to !== value.to) {
    setLastValue(value);
    setHeld(null);
  }
  const [hovered, setHovered] = React.useState<string | null>(null);
  const [focused, setFocused] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [tipX, setTipX] = React.useState(0);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, []);

  const shown = preview ?? held ?? value;
  const n = months.length;
  const cols = Math.max(n, 1);
  const from = Math.max(keys.indexOf(shown.from), 0);
  const to = Math.max(keys.indexOf(shown.to), from);
  const count = to - from + 1;

  const keyAt = (event: React.PointerEvent): string | null => {
    const rect = stripRef.current?.getBoundingClientRect();
    let index: number;
    if (rect && rect.width > 0) {
      index = Math.floor(((event.clientX - rect.left) / rect.width) * n);
    } else {
      const cell = (event.target as HTMLElement).closest<HTMLElement>(
        "[data-month-key]",
      );
      index = cell ? keys.indexOf(cell.dataset.monthKey ?? "") : -1;
      if (index === -1) return null;
    }
    return nearestSelectable(months, Math.min(Math.max(index, 0), n - 1));
  };

  const onPointerDown = (event: React.PointerEvent) => {
    if (event.button > 0) return;
    const key = keyAt(event);
    if (!key) return;
    const anchor =
      event.shiftKey && selectable.includes(value.from) ? value.from : key;
    brush.current = { anchor, head: key };
    setPreview(rangeBetween(selectable, anchor, key));
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const state = brush.current;
    if (!state) return;
    const key = keyAt(event);
    if (!key || key === state.head) return;
    state.head = key;
    setPreview(rangeBetween(selectable, state.anchor, key));
  };

  const onPointerUp = (event: React.PointerEvent) => {
    const state = brush.current;
    if (!state) return;
    brush.current = null;
    setPreview(null);
    const key = keyAt(event) ?? state.head;
    const next = rangeBetween(selectable, state.anchor, key);
    if (next.from !== value.from || next.to !== value.to) setHeld(next);
    onChange(next);
  };

  const cancel = () => {
    brush.current = null;
    setPreview(null);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const m = selectable.length;
    const first = selectable[0];
    const last = selectable[m - 1];
    if (first === undefined || last === undefined) return;
    let next: PeriodRange;
    if (event.key === "ArrowRight")
      next = stepRange(selectable, value, 1, event.shiftKey);
    else if (event.key === "ArrowLeft")
      next = stepRange(selectable, value, -1, event.shiftKey);
    else if (event.key === "Home" || event.key === "End") {
      const width = Math.max(
        selectable.indexOf(value.to) - selectable.indexOf(value.from),
        0,
      );
      next =
        event.key === "Home"
          ? { from: first, to: selectable[Math.min(width, m - 1)] ?? last }
          : { from: selectable[Math.max(m - 1 - width, 0)] ?? first, to: last };
    } else return;
    event.preventDefault();
    onChange(next);
  };

  // An open month under the pointer says what is left; otherwise the range's
  // end month does while the box has focus.
  const withHint = (key: string | null | undefined) => {
    const month = months.find((m) => m.key === key);
    return month?.status === "open" && month.hint ? month : null;
  };
  const tipMonth =
    withHint(hovered) ?? (focused ? withHint(months[to]?.key) : null);
  const endMonth = months[to];
  const endHint =
    endMonth?.status === "open" && endMonth.hint
      ? `. ${shortMonth(endMonth)}: ${endMonth.hint}`
      : "";
  const tipKey = tipMonth?.key ?? null;
  // The tooltip sits outside the scroller (which would clip it), centred under
  // its month as the month sits now, scroll included.
  React.useLayoutEffect(() => {
    if (!tipKey) return;
    const root = rootRef.current?.getBoundingClientRect();
    const cell = stripRef.current
      ?.querySelector<HTMLElement>(`[data-month-key="${tipKey}"]`)
      ?.getBoundingClientRect();
    if (root && cell) setTipX(cell.left + cell.width / 2 - root.left);
  }, [tipKey]);

  return (
    <div
      ref={rootRef}
      data-slot="period-timeline"
      className={cn(
        "relative max-w-full [container-type:inline-size]",
        className,
      )}
    >
      <div
        ref={scrollRef}
        className="max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div
          ref={stripRef}
          className="relative grid touch-pan-x select-none"
          style={{
            minWidth: n * MONTH_WIDTH,
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={cancel}
        >
          {months.map((month, i) => {
            const showYear = i === 0 || month.key.endsWith("-01");
            const before = month.status === "before_books";
            const picked = i >= from && i <= to && !before;
            const short = shortMonth(month);
            return (
              <div
                key={month.key}
                data-month-key={month.key}
                aria-disabled={before || undefined}
                title={
                  month.status && !month.hint
                    ? `${monthText(month)}, ${STATUS_WORD[month.status]}`
                    : undefined
                }
                className={cn(
                  "grid min-w-0 grid-rows-[16px_52px] items-center",
                  before ? "cursor-default" : "cursor-pointer",
                )}
                onPointerEnter={() => setHovered(month.key)}
                onPointerLeave={() =>
                  setHovered((current) =>
                    current === month.key ? null : current,
                  )
                }
              >
                <span className="truncate pl-3 text-2xs leading-none text-muted-foreground tabular-nums">
                  {showYear ? month.key.slice(0, 4) : null}
                </span>
                <span className="relative z-10 flex h-[52px] flex-col items-center justify-center gap-1">
                  <span
                    data-slot="month-label"
                    data-text={short}
                    className={cn(
                      // The ::after holds a bold copy, so bolding a pick never
                      // changes the label's width (no layout shift).
                      "type-figure inline-flex h-4 flex-col items-start justify-center text-sm leading-none after:invisible after:h-0 after:overflow-hidden after:font-semibold after:content-[attr(data-text)]",
                      before
                        ? "text-muted-foreground/50"
                        : month.hasData === false && !month.status
                          ? "text-muted-foreground"
                          : "text-foreground",
                      picked && "font-semibold",
                    )}
                  >
                    {short}
                  </span>
                  <span
                    data-slot="month-mark"
                    className="grid h-3 w-3.5 place-items-center"
                  >
                    <MonthMark status={month.status} />
                  </span>
                </span>
              </div>
            );
          })}
          {/*
          The box and its handles move by transform only (a container of the
          strip's width, measured in cqw), never by left: a transform is not a
          layout shift. It fills whole cells, inset 3px, at the chip's height,
          so its edges never touch a neighbour's label.
        */}
          <div className="pointer-events-none absolute inset-0 [container-type:inline-size]">
            <div
              role="slider"
              tabIndex={0}
              aria-label={ariaLabel}
              aria-orientation="horizontal"
              aria-valuemin={0}
              aria-valuemax={Math.max(n - 1, 0)}
              aria-valuenow={to}
              aria-valuetext={`${monthText(months[from])} to ${monthText(months[to])}${endHint}`}
              aria-describedby={tipMonth ? tipId : undefined}
              onKeyDown={onKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              className="absolute top-4 left-0 h-[52px] rounded-card bg-primary/10 ring-[1.5px] ring-primary ring-inset focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring"
              style={{
                width: `calc(${count} / ${cols} * 100cqw - 6px)`,
                transform: `translateX(calc(${from} / ${cols} * 100cqw + 3px))`,
              }}
            >
              <span
                data-handle="start"
                aria-hidden="true"
                className="absolute top-1/2 left-0 h-4 w-[3px] rounded-pill bg-primary"
                style={{ transform: "translate(5px, -50%)" }}
              />
              <span
                data-handle="end"
                aria-hidden="true"
                className="absolute top-1/2 left-0 h-4 w-[3px] rounded-pill bg-primary"
                style={{
                  transform: `translate(calc(${count} / ${cols} * 100cqw - 6px - 8px), -50%)`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {tipMonth ? (
        <div
          id={tipId}
          role="tooltip"
          className="pointer-events-none absolute top-full left-0 z-20 mt-1 w-max max-w-[min(20rem,100cqw)] rounded-control bg-popover px-2.5 py-1.5 text-compact text-popover-foreground shadow-md ring-1 ring-border"
          style={{
            // Centred under the month, kept inside the strip at either end.
            transform: `translateX(clamp(0px, calc(${tipX}px - 50%), calc(100cqw - 100%)))`,
          }}
        >
          {tipMonth.hint}
        </div>
      ) : null}
    </div>
  );
}

export { MonthMark, monthTodoText, PeriodTimeline, rangeBetween, stepRange };
export type {
  PeriodMonthStatus,
  PeriodMonthTodo,
  PeriodRange,
  PeriodTimelineMonth,
  PeriodTimelineProps,
};
